/**
 * useMomentum — React hook for consuming momentum signals
 *
 * Evaluates rules against current page context + entity data.
 * Returns signals, score, and top-level summary for the MomentumBar.
 *
 * Usage:
 *   const { score, signals, topSignals, enabled, toggle } = useMomentum({
 *     pageKey: 'hms.jobs.detail',
 *     entity: jobData,
 *     config: tenantMomentumConfig,
 *   });
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import type { MomentumSignal, MomentumScore, MomentumConfig } from './types';
import { sortSignals } from './types';
import { evaluateRules, evaluateListSignals } from './rule-engine';
import { computeMomentumScore } from './score';

// ─── LocalStorage key for user toggle ────────────────────────────────────────

const STORAGE_KEY = 'boox-momentum-enabled';

function getStoredEnabled(): boolean {
  if (typeof localStorage === 'undefined') return true;
  const v = localStorage.getItem(STORAGE_KEY);
  return v !== 'false'; // default: on
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface UseMomentumOpts {
  /** Current page key (e.g. 'hms.jobs.detail') */
  pageKey: string;
  /** Single entity data (detail/edit page) */
  entity?: Record<string, unknown>;
  /** Multiple entities (list page) */
  entities?: Record<string, unknown>[];
  /** Tenant momentum config (from API or defaults) */
  config?: Partial<MomentumConfig>;
}

export interface UseMomentumReturn {
  /** Whether momentum bar is toggled on */
  enabled: boolean;
  /** Toggle it on/off (persists to localStorage) */
  toggle: () => void;
  /** All raw signals for current context */
  signals: MomentumSignal[];
  /** Sorted signals (critical first) */
  sorted: MomentumSignal[];
  /** Top 3-5 most impactful signals */
  topSignals: MomentumSignal[];
  /** Aggregated score */
  score: MomentumScore;
  /** Convenience: any critical signals? */
  hasCritical: boolean;
  /** Convenience: total blocked revenue */
  blockedRevenue: number;
  /** Convenience: blocked currency */
  blockedCurrency: string;
}

export function useMomentum(opts: UseMomentumOpts): UseMomentumReturn {
  const { pageKey, entity, entities, config } = opts;

  const [enabled, setEnabled] = useState(getStoredEnabled);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  // Evaluate signals
  const signals = useMemo(() => {
    if (!enabled) return [];

    if (entities && entities.length > 0) {
      return evaluateListSignals(entities, pageKey, config);
    }
    if (entity) {
      return evaluateRules({ entity, pageKey }, config);
    }
    return [];
  }, [enabled, pageKey, entity, entities, config]);

  // Compute score
  const [previousScore, setPreviousScore] = useState<number | undefined>();

  const score = useMemo(
    () =>
      computeMomentumScore({
        signals,
        intent: config?.intent,
        weights: config?.weights,
        previousScore,
      }),
    [signals, config?.intent, config?.weights, previousScore],
  );

  // Track previous score for trend
  useEffect(() => {
    if (score.overall !== previousScore) {
      setPreviousScore(score.overall);
    }
  }, [score.overall, previousScore]);

  const sorted = useMemo(() => sortSignals(signals), [signals]);
  const topSignals = useMemo(() => sorted.slice(0, 5), [sorted]);

  const hasCritical = score.signalCounts.critical > 0;

  const blockedRevenue = useMemo(() => {
    return signals
      .filter((s) => s.type === 'revenue_blocked' && s.monetaryImpact)
      .reduce((sum, s) => sum + (s.monetaryImpact ?? 0), 0);
  }, [signals]);

  const blockedCurrency = signals.find((s) => s.type === 'revenue_blocked' && s.currency)?.currency ?? 'SGD';

  return {
    enabled,
    toggle,
    signals,
    sorted,
    topSignals,
    score,
    hasCritical,
    blockedRevenue,
    blockedCurrency,
  };
}
